import { elementMatchesSelector } from "./utils";

/**
 * Finds a valid proxy source element outside the current frame.
 *
 * @param frame - The remote-frame host element.
 * @param selector - Selector used to locate candidate proxy sources.
 * @param previousSource - Previously resolved source to reuse when still valid.
 * @returns A matching source element, or `null` when none are available.
 */
export function findProxySourceElement(
  frame: HTMLElement,
  selector: string,
  previousSource: Element | null
): Element | null {
  if (
    previousSource &&
    previousSource.isConnected &&
    elementMatchesSelector(previousSource, selector) &&
    !frame.contains(previousSource)
  ) {
    return previousSource;
  }

  let candidates: NodeListOf<Element>;
  try {
    candidates = document.querySelectorAll(selector);
  } catch {
    return null;
  }

  for (let i = 0; i < candidates.length; i++) {
    const candidate = candidates[i];
    if (candidate === frame) continue;
    if (frame.contains(candidate)) continue;
    return candidate;
  }

  return null;
}

/**
 * Serializes proxy source HTML while preserving live form-control state.
 *
 * @param source - Source DOM element to mirror.
 * @param select - Extraction mode (`inner` or `outer`).
 * @returns Serialized HTML string.
 */
export function serializeProxyHtml(source: Element, select: "inner" | "outer"): string {
  if (select === "inner") {
    return serializeInnerHtml(source);
  }

  const clone = source.cloneNode(true) as Element;
  syncFormControlState(source, clone);
  return clone.outerHTML;
}

/**
 * Serializes only child nodes from the source and preserves form state.
 *
 * @param source - Source DOM element to mirror.
 * @returns Serialized child HTML.
 */
function serializeInnerHtml(source: Element): string {
  const container = document.createElement("div");
  const sourceChildren = Array.from(source.childNodes);

  for (let i = 0; i < sourceChildren.length; i++) {
    const node = sourceChildren[i];
    const clone = node.cloneNode(true);

    if (node.nodeType === Node.ELEMENT_NODE && clone.nodeType === Node.ELEMENT_NODE) {
      syncFormControlState(node as Element, clone as Element);
    }

    container.appendChild(clone);
  }

  return container.innerHTML;
}

/**
 * Synchronizes source form-control state onto cloned controls.
 *
 * @param sourceRoot - Original source subtree.
 * @param cloneRoot - Cloned subtree to mutate before rendering.
 */
function syncFormControlState(sourceRoot: Element, cloneRoot: Element): void {
  const sourceControls = collectControls(sourceRoot);
  const cloneControls = collectControls(cloneRoot);
  const maxLen = Math.min(sourceControls.length, cloneControls.length);

  for (let i = 0; i < maxLen; i++) {
    syncSingleControl(sourceControls[i], cloneControls[i]);
  }
}

/**
 * Collects supported form controls from the root and descendants in DOM order.
 *
 * @param root - Root element to scan.
 * @returns Ordered control list.
 */
function collectControls(root: Element): HTMLElement[] {
  const controls: HTMLElement[] = [];

  if (isSupportedControl(root)) {
    controls.push(root as HTMLElement);
  }

  const nested = root.querySelectorAll("input,textarea,select,option");
  for (let i = 0; i < nested.length; i++) {
    controls.push(nested[i] as HTMLElement);
  }

  return controls;
}

/**
 * Checks whether a node is a control whose state must be mirrored.
 *
 * @param element - Candidate element.
 * @returns `true` when control state syncing applies.
 */
function isSupportedControl(element: Element): boolean {
  const tag = element.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || tag === "OPTION";
}

/**
 * Copies mutable control state between equivalent source and clone controls.
 *
 * @param source - Live source control element.
 * @param clone - Cloned control element that will be rendered.
 */
function syncSingleControl(source: HTMLElement, clone: HTMLElement): void {
  if (source instanceof HTMLInputElement && clone instanceof HTMLInputElement) {
    if (source.type !== "file") {
      clone.setAttribute("value", source.value);
    }

    if (source.checked) {
      clone.setAttribute("checked", "");
    } else {
      clone.removeAttribute("checked");
    }

    return;
  }

  if (source instanceof HTMLTextAreaElement && clone instanceof HTMLTextAreaElement) {
    clone.textContent = source.value;
    return;
  }

  if (source instanceof HTMLSelectElement && clone instanceof HTMLSelectElement) {
    const sourceOptions = source.options;
    const cloneOptions = clone.options;
    const maxLen = Math.min(sourceOptions.length, cloneOptions.length);

    for (let i = 0; i < maxLen; i++) {
      if (sourceOptions[i].selected) {
        cloneOptions[i].setAttribute("selected", "");
      } else {
        cloneOptions[i].removeAttribute("selected");
      }
    }
    return;
  }

  if (source instanceof HTMLOptionElement && clone instanceof HTMLOptionElement) {
    if (source.selected) {
      clone.setAttribute("selected", "");
    } else {
      clone.removeAttribute("selected");
    }
  }
}
